import React from 'react';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

interface Auth0ProviderWithNavigateProps {
  children: React.ReactNode;
  domain: string;
  clientId: string;
  redirectUri: string;
  audience?: string;
}

/**
 * Auth0 Provider wrapper that handles navigation after authentication
 * Following Auth0 best practices for React Router integration
 */
export const Auth0ProviderWithNavigate: React.FC<Auth0ProviderWithNavigateProps> = ({
  children,
  domain,
  clientId,
  redirectUri,
  audience
}) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    // Navigate to the URL the user was trying to access before authentication
    // or to the current pathname if no returnTo was set
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        // Use PKCE flow for enhanced security
        response_type: 'code',
        // Request appropriate scopes
        scope: 'openid profile email',
      }}
      // Use in-memory token storage for security (avoid localStorage)
      cacheLocation="memory"
      // Enable automatic token renewal
      useRefreshTokens={true}
      // Handle navigation after authentication
      onRedirectCallback={onRedirectCallback}
      // Enable iframe fallback when refresh tokens fail
      useRefreshTokensFallback={true}
    >
      {children}
    </Auth0Provider>
  );
};